import { useEffect, useState } from 'react';
import parseHTML from 'html-react-parser';
import { Avatar, Icon } from '@hotjar/hotsauce';
import { ErrorBoundary } from 'react-error-boundary';
import { useInAppMessage } from '../../hooks/useInAppMessage';
import { usePrevious } from '../../hooks/usePrevious';
import { captureException } from '../../error-tracking';

import {
  WidgetWrapper,
  WidgetTitleBar,
  Title,
  CloseButton,
  WidgetContent,
  WidgetCTASection,
  CTAButton,
} from './IterableWidget.styles';

const trimHtml = (html: string): string => {
  if (!html) return '';
  let trimmedHtml = '';
  trimmedHtml = html.replace(/<html>.+<body[^>]*>/gs, '');
  trimmedHtml = trimmedHtml.replace(/<\/body>.+<\/html>/gs, '');
  return trimmedHtml;
};

interface IterableWidgetProps {
  userId: number;
}

const IterableWidgetInner = ({ userId }: IterableWidgetProps): JSX.Element | null => {
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const previousIsWidgetOpen = usePrevious<boolean>(isWidgetOpen);
  const {
    displayedMessages,
    consumeMessage,
    trackMessageClose,
    trackMessageClick,
    trackMessageDelivery,
    trackMessageOpen,
  } = useInAppMessage(String(userId));

  useEffect(() => {
    if (displayedMessages.length > 0) {
      setIsWidgetOpen(true);
    }
  }, [displayedMessages]);

  useEffect(() => {
    // track corresponding message events when modal gets open
    if (isWidgetOpen && !previousIsWidgetOpen) {
      trackMessageDelivery(displayedMessages[0]);
      trackMessageOpen(displayedMessages[0]);
    }
  }, [
    displayedMessages,
    isWidgetOpen,
    previousIsWidgetOpen,
    trackMessageDelivery,
    trackMessageOpen,
  ]);

  useEffect(() => {
    if (displayedMessages.length > 0 && displayedMessages[0].content.payload.showOnce) {
      consumeMessage(displayedMessages[0], true);
    }
  }, [displayedMessages, consumeMessage]);

  const handleClose = () => {
    setIsWidgetOpen(false);
    trackMessageClose(displayedMessages[0]);
    consumeMessage(displayedMessages[0]);
  };

  if (!isWidgetOpen) return null;

  const [
    {
      content: { payload, html },
    },
  ] = displayedMessages;

  const handleCtaClick = () => {
    if (payload.ctaTarget) {
      trackMessageClick(displayedMessages[0], payload.ctaTarget);
      window.open(payload.ctaTarget);
    }
  };

  if (!userId) {
    return null;
  }

  return (
    <WidgetWrapper>
      <WidgetTitleBar>
        <Avatar
          src={payload.authorAvatar}
          altText="Author avatar"
          userName={payload.author}
          size="l"
        />
        <Title>
          <strong>{payload.author}</strong> from Hotjar
        </Title>
        <CloseButton onClick={handleClose} aria-label="Close">
          <Icon color="text_light" icon="close" size="m" />
        </CloseButton>
      </WidgetTitleBar>
      <WidgetContent hasCTA={!!payload.cta}>{parseHTML(trimHtml(html))}</WidgetContent>
      {payload.cta ? (
        <WidgetCTASection>
          <CTAButton onClick={handleCtaClick} kind="primary">
            {payload.cta}
          </CTAButton>
        </WidgetCTASection>
      ) : null}
    </WidgetWrapper>
  );
};

export const IterableWidget = (props: IterableWidgetProps): JSX.Element => {
  return (
    <ErrorBoundary fallback={<div />} onError={(error, info) => captureException(error, info)}>
      <IterableWidgetInner {...props} />
    </ErrorBoundary>
  );
};
